import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kb } from "@/lib/api";

export default function PastPerformance() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [viewing, setViewing] = useState(null);

  const load = async () => setItems(await kb.pastPerformance());
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!draft.title || !draft.agency) return;
    await kb.addPastPerformance({
      title: draft.title,
      agency: draft.agency,
      contract_number: draft.contract_number,
      description: draft.description,
      narrative: draft.narrative || draft.description,
      tags: (draft.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
      value: draft.value,
      period: draft.period,
      role: draft.role || "PRIME",
      cpar: draft.cpar || "Excellent",
    });
    setOpen(false); setDraft({});
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this past performance entry?")) return;
    await kb.delPastPerformance(id);
    await load();
  };

  const TAGS = ["ALL", "Defense", "Cloud", "Cybersecurity", "M365"];

  const filtered = items.filter((it) => {
    if (filter !== "ALL") {
      const tagBlob = `${it.title} ${(it.tags || []).join(" ")} ${it.agency}`.toLowerCase();
      if (!tagBlob.includes(filter.toLowerCase())) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const blob = `${it.title} ${it.agency} ${(it.tags || []).join(" ")} ${it.description || ""}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="page" data-testid="page-pp">
      <div className="page-header">
        <div>
          <div className="page-title">Past Performance</div>
          <div className="page-subtitle">Knowledge base for AI-powered content generation · {items.length} contracts on record</div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-primary btn-sm" onClick={() => { setOpen(true); setDraft({}); }} data-testid="add-pp-btn">
            + Add Contract
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap" style={{ maxWidth: 360 }}>
          <span className="search-icon">🔍</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search past performance…" data-testid="pp-search" />
        </div>
        {TAGS.map((t) => (
          <div
            key={t}
            className={`filter-chip ${filter === t ? "active" : ""}`}
            onClick={() => setFilter(t)}
            data-testid={`pp-filter-${t.toLowerCase()}`}
          >
            {t}
          </div>
        ))}
      </div>

      <div className="opp-cards">
        {filtered.map((it) => (
          <div className="kb-card" key={it.id} data-testid={`pp-card-${it.id}`}>
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <span className={`opp-type ${it.role === "SUBK" ? "rfi" : "rfp"}`} style={{ fontSize: 11 }}>
                {it.role || "PRIME"}
              </span>
              <span className={`pill ${it.cpar === "Excellent" ? "pill-green" : "pill-blue"}`}>{it.cpar || "Good"} CPAR</span>
            </div>
            <div className="kb-card-title">{it.title}</div>
            <div className="kb-card-meta">{it.agency}{it.contract_number ? ` · Contract #${it.contract_number}` : ""}</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.5 }}>
              {it.description}
            </div>
            <div className="kb-card-tags">
              {(it.tags || []).map((t, i) => (<span className="kb-tag" key={i}>{t}</span>))}
            </div>
            <div className="flex gap-8 mt-12">
              <button className="btn btn-ghost btn-sm" onClick={() => setViewing(it)} data-testid={`pp-view-${it.id}`}>
                📋 View Write-up
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate("/workspace")} title="Open the workspace to reference this in a section" data-testid={`pp-use-${it.id}`}>🤖 Use in Proposal</button>
              <button className="btn btn-ghost btn-sm" onClick={() => remove(it.id)} style={{ marginLeft: "auto" }} data-testid={`pp-delete-${it.id}`}>
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="pp-modal">
            <h3>Add Past Performance</h3>
            <div className="modal-sub">Contract narratives are indexed for AI-powered RAG drafting</div>
            <label>Title *</label>
            <input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="pp-title" />
            <label>Agency *</label>
            <input value={draft.agency || ""} onChange={(e) => setDraft({ ...draft, agency: e.target.value })} data-testid="pp-agency" />
            <label>Contract Number</label>
            <input value={draft.contract_number || ""} onChange={(e) => setDraft({ ...draft, contract_number: e.target.value })} />
            <label>Short Description</label>
            <input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <label>Full Narrative</label>
            <textarea rows={4} value={draft.narrative || ""} onChange={(e) => setDraft({ ...draft, narrative: e.target.value })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <div><label>Value</label><input value={draft.value || ""} onChange={(e) => setDraft({ ...draft, value: e.target.value })} placeholder="$3.2M" /></div>
              <div><label>Period</label><input value={draft.period || ""} onChange={(e) => setDraft({ ...draft, period: e.target.value })} placeholder="2022-2024" /></div>
              <div>
                <label>Role</label>
                <select value={draft.role || "PRIME"} onChange={(e) => setDraft({ ...draft, role: e.target.value })}><option>PRIME</option><option>SUBK</option></select>
              </div>
            </div>
            <label>Tags (comma-separated)</label>
            <input value={draft.tags || ""} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} placeholder="M365, Cybersecurity, DoD" />
            <div className="flex gap-8" style={{ marginTop: 18, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={submit} data-testid="pp-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal-backdrop" onClick={() => setViewing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }} data-testid="pp-detail-modal">
            <h3>{viewing.title}</h3>
            <div className="modal-sub">{viewing.agency} · {viewing.contract_number}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {(viewing.tags || []).map((t, i) => <span className="kb-tag" key={i}>{t}</span>)}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--text)" }}>
              {viewing.narrative || viewing.description}
            </div>
            <div className="flex gap-8" style={{ marginTop: 18, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
